/**
 * Invite user hook
 * Handles inviting Manager and Staff users (Company administrators only)
 */

import { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { ROLE_PERMISSIONS } from '../../../shared/constants/Permissions';
import { functions } from '../config/firebase';
import { getErrorMessage } from '@shared/utils/error';
import { generatePassword } from '@shared/utils/password';
import type { Company } from '../../../shared/types';
import type { CreateUserResponse } from '../types/cloudFunctions.types';

export interface InviteUserFormData {
  email: string;
  firstName: string;
  lastName: string;
  countryCode?: string;
  phoneNumber?: string;
  userType: 'manager' | 'staff';
  managedStaffIds?: string[]; // Only for managers
}

export interface InviteUserResult {
  success: boolean;
  password?: string;
  userId?: string;
}

export const useInviteUser = (company: Company) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const inviteUser = async (formData: InviteUserFormData): Promise<InviteUserResult> => {
    setLoading(true);
    setError('');

    try {
      // Generate password
      const password = generatePassword();

      // Get permissions based on user type
      const permissions = formData.userType === 'manager'
        ? ROLE_PERMISSIONS.manager
        : ROLE_PERMISSIONS.staff;

      // Call Cloud Function to create user account
      // This uses Admin SDK to create both Firebase Auth and Firestore documents
      const createUserAccount = httpsCallable<unknown, CreateUserResponse>(functions, 'createUserAccount');
      const result = await createUserAccount({
        email: formData.email,
        password,
        firstName: formData.firstName,
        lastName: formData.lastName,
        countryCode: formData.countryCode || '+353',
        phoneNumber: formData.phoneNumber || '',
        userType: formData.userType,
        companyId: company.id,
        permissions,
        managedStaffIds: formData.managedStaffIds || [],
      });

      const userId = result.data.userId;

      return {
        success: true,
        password,
        userId,
      };
    } catch (err) {
      const message = getErrorMessage(err);
      setError(message);

      return {
        success: false,
      };
    } finally {
      setLoading(false);
    }
  };

  return {
    inviteUser,
    loading,
    error,
  };
};
